<script setup lang="ts">
/**
 * 模型设置弹窗组件
 * 用于选择默认图片/视频模型及视频参数
 */
import { ref, computed, watch } from 'vue';
import { Modal, Button, Tag, Select, Spin } from 'ant-design-vue';
import { ThunderboltOutlined, StarOutlined, DollarOutlined } from '@ant-design/icons-vue';
import { useModelStore } from '@/stores/models';
import type { VideoModelParams } from '@/api/models';
import { snapDuration } from '@/utils/snap-duration';
import { VIDEO_CREDITS_RULES, calculateVideoCreditsCost } from '@ai-video/shared';

/** 按时长计费的视频模型 */
const DURATION_BILLED_VIDEO_MODEL_IDS = new Set([
  'seedance-1.0-lite',
  'seedance2.0-video-fast-480p',
  'seedance2.0-video-720p',
  'seedance2.0-video-1080p',
]);

/** 判断是否为按时长计费的视频模型 */
const isDurationBilledVideo = (modelId: string) => DURATION_BILLED_VIDEO_MODEL_IDS.has(modelId);

interface Props {
  open: boolean;
}

const props = defineProps<Props>();
const emit = defineEmits<{
  (e: 'update:open', val: boolean): void;
}>();

const modelStore = useModelStore();
const saving = ref(false);

// 本地编辑状态（打开弹窗时从 store 复制）
const localImageModel = ref('');
const localVideoModel = ref('');
const localDuration = ref(5);
const localResolution = ref('720p');

// 预览模型ID列表（仅展示积分信息，不可选择）
const previewModelIds = ['preview-character', 'preview-location'];

// 可选择的图片模型
const selectableImageModels = computed(() =>
  modelStore.imageModels.filter(m => !previewModelIds.includes(m.id)),
);

// 预览模型（仅展示信息）
const previewModels = computed(() =>
  modelStore.imageModels.filter(m => previewModelIds.includes(m.id)),
);

// 当前选中的视频模型配置
const selectedVideoConfig = computed(() =>
  modelStore.videoModels.find(m => m.id === localVideoModel.value),
);

// 视频模型参数
const videoParams = computed(() =>
  selectedVideoConfig.value?.params as VideoModelParams | undefined,
);

/** 当前视频模型根据参数计算的积分消耗 */
const selectedVideoCredits = computed(() =>
  calculateVideoCreditsCost(localDuration.value, localResolution.value, localVideoModel.value),
);

// 速度标签颜色映射
const speedColor: Record<string, string> = { fast: 'green', medium: 'orange', slow: 'red' };
const speedLabel: Record<string, string> = { fast: '快速', medium: '中速', slow: '较慢' };

// 质量标签颜色映射
const qualityColor: Record<string, string> = { standard: 'default', high: 'blue', premium: 'purple' };
const qualityLabel: Record<string, string> = { standard: '标准', high: '高质量', premium: '顶级' };

// 弹窗打开时初始化
watch(() => props.open, async (val) => {
  if (!val) return;
  // 确保模型列表已加载
  if (modelStore.imageModels.length === 0) await modelStore.fetchModels();
  await modelStore.fetchPreferences();
  // 复制偏好到本地
  localImageModel.value = modelStore.preferences.defaultImageModel || 'seedream-3.0';
  localVideoModel.value = modelStore.preferences.defaultVideoModel || 'seedance-1.0-lite';
  localDuration.value = modelStore.preferences.defaultVideoDuration || 5;
  localResolution.value = modelStore.preferences.defaultVideoResolution || '720p';
});

// 切换视频模型时自动就近匹配时长
watch(localVideoModel, (newId, oldId) => {
  if (!newId || newId === oldId) return;
  const model = modelStore.videoModels.find(m => m.id === newId);
  if (!model) return;
  const params = model.params as VideoModelParams;
  // 就近匹配时长
  localDuration.value = snapDuration(localDuration.value, params.supportedDurations);
  // 分辨率不在支持列表中时取第一个
  if (!params.supportedResolutions.includes(localResolution.value)) {
    localResolution.value = params.supportedResolutions[0] || '720p';
  }
});

/** 保存偏好 */
const handleSave = async () => {
  if (saving.value) return;
  saving.value = true;
  const ok = await modelStore.updatePreferences({
    defaultImageModel: localImageModel.value,
    defaultVideoModel: localVideoModel.value,
    defaultVideoDuration: localDuration.value,
    defaultVideoResolution: localResolution.value,
  });
  saving.value = false;
  if (ok) emit('update:open', false);
};

/** 关闭弹窗 */
const handleCancel = () => {
  emit('update:open', false);
};
</script>

<template>
  <Modal
    :open="open"
    title="模型选项"
    :width="640"
    :footer="null"
    @cancel="handleCancel"
  >
    <Spin :spinning="modelStore.loading">
      <div class="model-settings">
        <!-- 图片模型选择 -->
        <div class="section">
          <div class="section-title">图片模型</div>
          <div class="model-cards">
            <div
              v-for="model in selectableImageModels"
              :key="model.id"
              :class="['model-card', { active: localImageModel === model.id }]"
              @click="localImageModel = model.id"
            >
              <div class="model-name">{{ model.name }}</div>
              <div class="model-desc">{{ model.description }}</div>
              <div class="model-tags">
                <Tag :color="speedColor[model.speed]" size="small">
                  <ThunderboltOutlined /> {{ speedLabel[model.speed] }}
                </Tag>
                <Tag :color="qualityColor[model.quality]" size="small">
                  <StarOutlined /> {{ qualityLabel[model.quality] }}
                </Tag>
                <Tag color="gold" size="small">
                  <DollarOutlined /> {{ model.creditsCost }} 积分
                </Tag>
              </div>
            </div>
          </div>
          <!-- 预览模型积分消耗说明 -->
          <div v-if="previewModels.length > 0" class="model-cards preview-cards">
            <div
              v-for="model in previewModels"
              :key="model.id"
              class="model-card preview-card"
            >
              <div class="model-header">
                <span class="model-name">{{ model.name }}</span>
                <Tag size="small" color="blue">积分消耗</Tag>
              </div>
              <div class="model-desc">{{ model.description }}</div>
              <div class="model-tags">
                <Tag color="gold" size="small">
                  <DollarOutlined /> {{ model.creditsCost }} 积分/次
                </Tag>
              </div>
            </div>
          </div>
        </div>

        <!-- 视频模型选择 -->
        <div class="section">
          <div class="section-title">视频模型</div>
          <div class="model-cards">
            <div
              v-for="model in modelStore.videoModels"
              :key="model.id"
              :class="['model-card', { active: localVideoModel === model.id }]"
              @click="localVideoModel = model.id"
            >
              <div class="model-name">{{ model.name }}</div>
              <div class="model-desc">{{ model.description }}</div>
              <div class="model-tags">
                <Tag :color="speedColor[model.speed]" size="small">
                  <ThunderboltOutlined /> {{ speedLabel[model.speed] }}
                </Tag>
                <Tag :color="qualityColor[model.quality]" size="small">
                  <StarOutlined /> {{ qualityLabel[model.quality] }}
                </Tag>
                <!-- 按时长计费视频：显示计费规则和动态积分 -->
                <template v-if="isDurationBilledVideo(model.id)">
                  <Tag
                    v-for="resolution in ((model.params as VideoModelParams).supportedResolutions || [])"
                    :key="`${model.id}-${resolution}`"
                    color="blue"
                    size="small"
                  >
                    {{ resolution }}: {{ VIDEO_CREDITS_RULES.models[model.id] ?? VIDEO_CREDITS_RULES.resolutions[resolution] ?? 1 }}积分/秒
                  </Tag>
                  <Tag color="gold" size="small">
                    <DollarOutlined /> {{ model.id === localVideoModel ? selectedVideoCredits : model.creditsCost }} 积分
                  </Tag>
                </template>
                <!-- 其他视频模型：显示固定积分 -->
                <Tag v-else color="gold" size="small">
                  <DollarOutlined /> {{ model.creditsCost }} 积分
                </Tag>
              </div>
            </div>
          </div>
        </div>

        <!-- 视频参数 -->
        <div v-if="videoParams" class="section">
          <div class="section-title">视频参数</div>
          <div class="params-row">
            <div class="param-item">
              <label>默认时长</label>
              <Select v-model:value="localDuration" size="small" style="width: 100px">
                <Select.Option
                  v-for="d in videoParams.supportedDurations"
                  :key="d"
                  :value="d"
                >{{ d }}秒</Select.Option>
              </Select>
            </div>
            <div class="param-item">
              <label>默认分辨率</label>
              <Select v-model:value="localResolution" size="small" style="width: 100px">
                <Select.Option
                  v-for="r in videoParams.supportedResolutions"
                  :key="r"
                  :value="r"
                >{{ r }}</Select.Option>
              </Select>
            </div>
          </div>
        </div>

        <!-- 操作按钮 -->
        <div class="footer-actions">
          <Button @click="handleCancel">取消</Button>
          <Button type="primary" :loading="saving" @click="handleSave">保存</Button>
        </div>
      </div>
    </Spin>
  </Modal>
</template>

<style lang="scss" scoped>
.model-settings {
  .section {
    margin-bottom: 20px;
    .section-title {
      font-size: 14px;
      font-weight: 600;
      color: var(--text-primary, #fff);
      margin-bottom: 10px;
    }
  }
}

.model-cards {
  display: flex;
  gap: 12px;
  flex-wrap: wrap;
}

.model-card {
  flex: 1;
  min-width: 160px;
  padding: 12px;
  border: 2px solid var(--border-color, #333);
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.2s;
  background: var(--bg-tertiary, #2a2a2a);

  &:hover { border-color: var(--color-primary, #0066FF); }
  &.active {
    border-color: var(--color-primary, #0066FF);
    background: rgba(0, 102, 255, 0.08);
  }

  .model-name {
    font-size: 14px;
    font-weight: 600;
    color: var(--text-primary, #fff);
    margin-bottom: 4px;
  }
  .model-desc {
    font-size: 12px;
    color: var(--text-tertiary, #666);
    margin-bottom: 8px;
  }
  .model-tags {
    display: flex;
    flex-wrap: wrap;
    gap: 4px;
  }
}

.params-row {
  display: flex;
  gap: 24px;
  .param-item {
    display: flex;
    align-items: center;
    gap: 8px;
    label {
      font-size: 13px;
      color: var(--text-secondary, #999);
      white-space: nowrap;
    }
  }
}

.preview-cards {
  margin-top: 12px;
}

.preview-card {
  cursor: default !important;
  border-style: dashed !important;
  
  &:hover {
    border-color: var(--border-color, #333) !important;
  }
  
  .model-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: 4px;
  }
}

.footer-actions {
  display: flex;
  justify-content: flex-end;
  gap: 8px;
  margin-top: 16px;
  padding-top: 16px;
  border-top: 1px solid var(--border-color, #333);
}
</style>
